/* globals gon, PropTypes */

import { useEffect, useMemo, useRef, useState } from 'react'
import { TreeSelect, Checkbox } from 'antd'
import Core from '@hcm4all/frontend-core'

const { ChevronDown, ChevronRight20: ChevronRight } = Core.UI.SVG.Icons
const { ChevronDown: ChevronDown16, SearchSm, XClose } = Core.UI.SVG.Icons16

const optionsRequestCache = new Map()

function fetchOptions (path, dashboard) {
  const url = new URL(path, window.location.origin)
  if (dashboard) url.searchParams.set('dashboard', dashboard)
  const cacheKey = url.toString()

  if (!optionsRequestCache.has(cacheKey)) {
    const request = fetch(url).then(res => {
      if (!res.ok) throw new Error(`Failed to fetch options: ${res.status}`)
      return res.json()
    })
    request.catch(() => optionsRequestCache.delete(cacheKey))
    optionsRequestCache.set(cacheKey, request)
  }
  return optionsRequestCache.get(cacheKey)
}

function isGroupedTree (options) {
  return Array.isArray(options) && options.length > 0 && Array.isArray(options[0]?.[1])
}

function buildFlatTreeData (options, field) {
  return (options || []).flatMap(pair => {
    if (!Array.isArray(pair) || pair.length < 2) {
      console.warn(`Wrong data has been received from /search/fetch_options/${field}. Expected an array of pairs instead ${typeof pair}`)
      return []
    }
    const [label, value] = pair
    return [{ title: label, value: String(value), key: String(value) }]
  })
}

function buildGroupedTreeData (options, keyToRealValue) {
  let seq = 0

  const buildGroup = ([groupName, items], groupIndex) => {
    const byId = new Map(
      (items || []).map(([name, id, meta]) => [
        String(id),
        { name, id, meta: meta || {}, children: [] }
      ])
    )

    const roots = []
    byId.forEach(item => {
      const parentId = item.meta.parent_id
      const parent = parentId != null && byId.has(String(parentId)) ? byId.get(String(parentId)) : null
      if (parent && parent !== item) {
        parent.children.push(item)
      } else {
        roots.push(item)
      }
    })

    const toNode = (item) => {
      const key = `item_${seq++}_${item.id}`
      keyToRealValue[key] = String(item.id)
      return {
        title: item.name,
        value: key,
        key,
        className: item.meta.class,
        children: item.children.length ? item.children.map(toNode) : undefined
      }
    }

    const groupKey = `group_${groupIndex}`
    return {
      title: groupName,
      value: groupKey,
      key: groupKey,
      className: 'multiselect-tree-select-unit',
      children: roots.map(toNode)
    }
  }

  return (options || []).map(buildGroup)
}

function getSelectableValues (nodes) {
  return nodes.flatMap((node) => [
    ...(node.disabled ? [] : [node.value]),
    ...(node.children ? getSelectableValues(node.children) : [])
  ])
}

function collectDescendantKeys (node) {
  if (!node?.children) return []
  return node.children.flatMap(child => [child.value, ...collectDescendantKeys(child)])
}

function flattenNodesByKey (nodes, map = new Map()) {
  nodes.forEach(node => {
    map.set(node.value, node)
    if (node.children) flattenNodesByKey(node.children, map)
  })
  return map
}

export default function MultiselectInput (props) {
  const {
    name, select_options: selectOptionsProp, select_options_path: selectOptionsPath,
    select_options_full_path: selectOptionsFullPath, switchable, placeholder, field, id,
    value: valueProp, disable_filtering: disableFiltering, onChange: onChangeProp,
    popup_class_name: popupClassName
  } = props

  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [exclude, setExclude] = useState(false)
  const [selectedKeys, setSelectedKeys] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const requestRef = useRef(null)
  const initializedRef = useRef(false)

  const isOptionsPresent = options && options.length > 0
  const isWorkingLocation = field === 'working_location'
  const withoutSwitchableMultiselect = !Object.prototype.hasOwnProperty.call(props, 'switchable') ? 'without-switchable-multiselect' : ''

  const treeShaped = useMemo(() => isGroupedTree(options), [options])

  const { treeData, keyToRealValue } = useMemo(() => {
    if (treeShaped) {
      const map = {}
      return { treeData: buildGroupedTreeData(options, map), keyToRealValue: map }
    }
    return { treeData: buildFlatTreeData(options, field), keyToRealValue: {} }
  }, [options, treeShaped, field])

  const selectableValues = useMemo(
    () => getSelectableValues(treeData).filter(key => !treeShaped || key in keyToRealValue),
    [treeData, treeShaped, keyToRealValue]
  )

  const keyToNode = useMemo(() => flattenNodesByKey(treeData), [treeData])

  const treeSelectValue = useMemo(() => selectedKeys.map(key => ({ value: key })), [selectedKeys])

  const realValueToKeys = useMemo(() => {
    const map = new Map()
    Object.entries(keyToRealValue).forEach(([key, real]) => {
      if (!map.has(real)) map.set(real, [])
      map.get(real).push(key)
    })
    return map
  }, [keyToRealValue])

  const realSelectedValues = useMemo(() => {
    const seen = new Set()
    const result = []
    selectedKeys.forEach(key => {
      const real = treeShaped ? keyToRealValue[key] : key
      if (real != null && !seen.has(real)) {
        seen.add(real)
        result.push(real)
      }
    })
    return result
  }, [selectedKeys, treeShaped, keyToRealValue])

  useEffect(() => {
    let cancelled = false

    if (selectOptionsProp) {
      setOptions(selectOptionsProp)
      setLoading(false)
    } else if (selectOptionsPath || selectOptionsFullPath) {
      const url = selectOptionsFullPath || `${selectOptionsPath}${field}`
      const request = fetchOptions(url, gon?.dashboard)
      requestRef.current = request
      request.then(data => {
        if (cancelled || requestRef.current !== request) return
        setOptions(data || [])
        setLoading(false)
      }).catch(() => {
        if (cancelled || requestRef.current !== request) return
        setOptions([])
        setLoading(false)
      })
    } else {
      setLoading(false)
    }

    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (loading || initializedRef.current) return
    initializedRef.current = true

    let initialValues = []
    let initialExclude = false

    if (switchable && valueProp && _.isObject(valueProp) && !_.isArray(valueProp)) {
      initialExclude = Object.prototype.hasOwnProperty.call(valueProp, 'exclude')
      initialValues = valueProp.include || valueProp.exclude || []
    } else if (valueProp && _.isArray(valueProp)) {
      initialValues = valueProp
    } else if (valueProp) {
      initialValues = [valueProp]
    }

    setExclude(initialExclude)
    setSelectedKeys(
      [].concat(initialValues)
        .map(v => keyForRealValue(v))
        .filter(Boolean)
    )
  }, [loading])

  useEffect(() => {
    if (!isWorkingLocation || loading || !options.length) return

    const defaultPair = options.length === 1
      ? options[0]
      : options.find(([label]) => label === valueProp) || options[0]

    if (!defaultPair) return
    const key = keyForRealValue(defaultPair[1])
    if (!key) return

    setSelectedKeys(current => (current.length ? current : [key]))
  }, [isWorkingLocation, loading, options])

  const workingLocationHidden = isWorkingLocation && (loading || options.length <= 1)

  useEffect(() => {
    if (!isWorkingLocation || workingLocationHidden) return
    document.getElementById(`${field}_label`)?.classList.remove('d-none')
  }, [isWorkingLocation, workingLocationHidden])

  useEffect(() => {
    if (!initializedRef.current) return
    onChangeProp && onChangeProp(realSelectedValues)
  }, [realSelectedValues, onChangeProp])

  const fieldName = () => {
    if (!switchable) return name
    const prefix = exclude ? 'exclude_' : 'include_'
    const parts = name.split('[')
    if (parts.length > 1) {
      parts[1] = prefix + parts[1]
      return parts.join('[')
    }
    return prefix + name
  }

  const keyForRealValue = (real) => {
    if (!treeShaped) return String(real)
    const keys = realValueToKeys.get(String(real))
    return keys ? keys[0] : null
  }

  const handleChange = (nextValue, _labels, extra) => {
    const nextKeys = new Set((nextValue || []).map(item => (item && typeof item === 'object' ? item.value : item)))
    const triggerKey = extra?.triggerValue
    if (triggerKey != null) {
      const descendantKeys = collectDescendantKeys(keyToNode.get(triggerKey))
      descendantKeys.forEach(key => (extra.checked ? nextKeys.add(key) : nextKeys.delete(key)))
    }
    setSelectedKeys([...nextKeys])
  }

  const allSelected =
    selectableValues.length > 0 &&
    selectableValues.every((item) => selectedKeys.includes(item))

  const someSelected =
    !allSelected &&
    selectableValues.some((item) => selectedKeys.includes(item))

  const handleSelectAll = () => {
    setSelectedKeys(allSelected ? [] : selectableValues)
  }

  const popupRender = (menu) => (
    <>
      {!disableFiltering && (
        <div
          className='multiselect-tree-search'
          onClick={(event) => event.stopPropagation()}
        >
          <SearchSm className='multiselect-tree-search-icon' />
          <input
            ref={(el) => el?.focus()}
            type='text'
            className='multiselect-tree-search-input'
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder={I18n.t('searches.labels.multiselect.filter_placeholder')}
          />
          {searchValue && (
            <button
              type='button'
              className='multiselect-tree-search-clear'
              onClick={() => setSearchValue('')}
            >
              <XClose />
            </button>
          )}
        </div>
      )}
      <div className='multiselect-tree-select-menu'>
        {isOptionsPresent && (
          <div
            className='multiselect-tree-select-all'
            onClick={(event) => event.stopPropagation()}
          >
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={handleSelectAll}
            >
              {I18n.t('searches.labels.multiselect.select_all_text')}
            </Checkbox>
          </div>
        )}

        {menu}
      </div>
    </>
  )

  return (
    <div
      id={field}
      className={`labelholder show-labelholder ${withoutSwitchableMultiselect} ${switchable ? 'switchable-multiselect' : ''} ${exclude ? 'exclude' : ''} ${workingLocationHidden ? 'd-none' : ''}`}
      data-label={placeholder || ''}
    >
      {switchable && (
        <button
          className='multiselect-switcher'
          onClick={(e) => { e.preventDefault(); setExclude(!exclude) }}
          title={exclude ? I18n.t('searches.labels.multiselect.exclude') : I18n.t('searches.labels.multiselect.include')}
        >
          <i className={exclude ? 'fa fa-ban' : 'fa fa-check'}> </i>
        </button>
      )}
      <TreeSelect
        id={id}
        className='select form-control multiselect-tree-select'
        classNames={{ popup: { root: ['multiselect-tree-select-dropdown', popupClassName].filter(Boolean).join(' ') } }}
        treeData={treeData}
        value={treeSelectValue}
        onChange={handleChange}
        treeCheckable
        treeCheckStrictly
        treeDefaultExpandAll
        showSearch={!disableFiltering}
        searchValue={searchValue}
        onSearch={setSearchValue}
        filterTreeNode={(input, node) => (node.title || '').toLowerCase().includes(input.toLowerCase())}
        loading={loading}
        placeholder={placeholder || ''}
        maxTagCount={0}
        maxTagPlaceholder={() => `${realSelectedValues.length} ${I18n.t('searches.labels.multiselect.selected_text')}`}
        allowClear
        popupRender={popupRender}
        onOpenChange={(open) => { if (!open) setSearchValue('') }}
        suffixIcon={<ChevronDown16 className='multiselect-tree-select-arrow' />}
        switcherIcon={({ expanded }) => (
          expanded
            ? <ChevronDown className='multiselect-tree-switcher-icon' />
            : <ChevronRight className='multiselect-tree-switcher-icon' />
        )}
        popupMatchSelectWidth={false}
        style={{ width: '100%' }}
      />
      {realSelectedValues.map(v => (
        <input key={v} type='hidden' name={`${fieldName()}[]`} value={v} />
      ))}
    </div>
  )
}

MultiselectInput.propTypes = {
  name: PropTypes.string.isRequired,
  select_options: PropTypes.arrayOf(PropTypes.array), // [[text, value], [text, value]]
  select_options_path: PropTypes.string,
  select_options_full_path: PropTypes.string,
  onChange: PropTypes.func,
  switchable: PropTypes.bool,
  placeholder: PropTypes.string,
  field: PropTypes.string.isRequired, // field id, almost the same name
  id: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.string]),
  disable_filtering: PropTypes.bool,
  popup_class_name: PropTypes.string // extra class on the dropdown, e.g. for scoped z-index overrides
}
